import { ApiClient, ApiClientConfig } from "../api-client";
import { ApiClientError, handleApiErrorWithSchema } from "../errors";
import schemaValidators from "./schema-validators";
import { ApiClientProctoringAssessmentConfigurationV2, ApiClientProctoringOptionV2, AssessmentInfo } from "../types";

type StandaloneOptions = {
	studentInfoEntries: {
		[key: string]: string;
		email: string;
		firstName: string;
		lastName: string;
	}[];
	instructionTemplate?: string;
	emailBody?: string;
	expiryDate?: Date;
	exemptedEmails: string[];
};

export default class ProctoringConfigurationClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClientConfig) {
		super(config, schemaValidators);
	}

	get = async (assessmentInfo: AssessmentInfo) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/assessment/configuration/${assessmentInfo?.lms}/${assessmentInfo.hostname}/${
					assessmentInfo.courseId ?? ""
				}/${assessmentInfo.quizId}`,
				{ credentials: "include" },
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.proctoringAssessmentConfigurationV2OrNull.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	save = async ({
		assessmentInfo,
		courseInfo,
		profileId,
		proctoringAssessmentConfiguration,
		proctoringOption,
		standaloneOptions,
	}: {
		assessmentInfo: AssessmentInfo;
		courseInfo?: {
			name?: string;
			code?: string;
		};
		profileId?: number;
		proctoringAssessmentConfiguration?:
			| Partial<Omit<ApiClientProctoringAssessmentConfigurationV2, "proctoringOptionProfile">>
			| undefined;
		proctoringOption?: Partial<ApiClientProctoringOptionV2> | undefined;
		standaloneOptions?: StandaloneOptions;
	}) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/assessment/configuration/${assessmentInfo?.lms}/${assessmentInfo.hostname}/${
					assessmentInfo.courseId ?? ""
				}/${assessmentInfo.quizId}`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						assessmentInfo: assessmentInfo,
						courseInfo: courseInfo,
						profileId: profileId,
						proctoringAssessmentConfiguration: proctoringAssessmentConfiguration,
						proctoringOption: proctoringOption,
						standaloneOptions: standaloneOptions,
					}),
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.proctoringAssessmentConfigurationResponse.validate(obj)) {
					return obj;
				}
			}
			if (response.status === 413) {
				const payloadError = new Error("Payload too large");
				throw new ApiClientError(payloadError);
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	updateWhitelist = async ({ courseId, quizId, type }: { courseId: string; quizId: string; type?: string }) => {
		try {
			let url = `${this.config.verityOrigin}/api/verity/assessment/update-whitelist/${courseId}/${quizId}`;
			if (type) {
				url += `?type=${type}`;
			}
			await super.fetch(url, {
				method: "PUT",
				credentials: "include",
			});
		} catch (error) {
			console.error(error);
		}
		return;
	};

	getEnabledOptions = ({
		assessmentInfo,
		courseName,
	}: {
		assessmentInfo: Omit<AssessmentInfo, "quizId">;
		courseName?: string;
	}) => {
		let url = `${this.config.verityOrigin}/api/verity/assessment/configuration/${assessmentInfo?.lms}/${assessmentInfo.hostname}/${
			assessmentInfo.courseId ?? ""
		}/enabled-options`;
		if (courseName) {
			url += `?courseName=${encodeURIComponent(courseName)}`;
		}
		return handleApiErrorWithSchema(
			super.fetch(url, {
				credentials: "include",
			}),
			this.schemaValidators.enabledOptionsResponse,
		);
	};
}
